import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GradientIcon from '@mui/icons-material/Gradient';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_COLOR_GRADE_STATE,
  buildColorGradeRequest,
  hasSourceImage,
  type ColorGradeFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { ReferenceImageInput } from '../references/ReferenceImageInput';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

export const ColorGradePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<ColorGradeFormState>(DEFAULT_COLOR_GRADE_STATE);
  const [referenceRef, setReferenceRef] = useState<SourceImageRef | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.colorGrade);

  const hasSource = hasSourceImage(source);
  const styleOk = state.gradingStyle.trim().length > 0;

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to color-grade.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildColorGradeRequest(state, sid, source, referenceRef);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasSource || !styleOk;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <GradientIcon fontSize="small" />
        <Typography variant="subtitle2">Color grade</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to grade.</Alert>
      )}

      <TextField
        label="Grading style"
        value={state.gradingStyle}
        onChange={e => setState(s => ({ ...s, gradingStyle: e.target.value }))}
        placeholder="e.g., warm cinematic teal & orange"
        multiline
        minRows={1}
        maxRows={3}
        size="small"
        fullWidth
        required
      />

      <ReferenceImageInput
        label="Palette reference (optional)"
        helperText="Sample colors from this image"
        value={referenceRef}
        onChange={setReferenceRef}
      />

      <Box>
        <Typography variant="caption" color="text.secondary">
          Strength · {state.strength.toFixed(2)}
        </Typography>
        <Slider
          value={state.strength}
          onChange={(_, v) => setState(s => ({ ...s, strength: Array.isArray(v) ? v[0] : v }))}
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Color grade failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <GradientIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Grading…' : 'Apply grade'}
        </Button>
      </Box>
    </Stack>
  );
};
